import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { HardDrive, Link2, Unlink, Check, ExternalLink, Loader2, Calendar, User, Save, Mail, Bot, Sparkles, Building2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Super admin email for special permissions
const SUPER_ADMIN_EMAIL = "palavancaner@gmail.com";

// Tenant-specific Drive folder links
const DRIVE_FOLDER_LINKS = {
  "royalcert": "https://drive.google.com/drive/folders/1f8Vf5UYvyrS6wFoYacppEN8X3pxxrnFY",
  "trb": "https://drive.google.com/drive/folders/19nRxZTlsYlMhfPvY5MoyANNdrOyV05OX",
  "default": "https://drive.google.com/drive/folders/19nRxZTlsYlMhfPvY5MoyANNdrOyV05OX"
};

// Get current tenant from hostname
const getCurrentTenantSlug = () => {
  const hostname = window.location.hostname;
  if (hostname.includes("royalcert")) return "royalcert";
  if (hostname.includes("trb")) return "trb";
  return "default";
};

const SettingsPage = () => {
  const { user, setUser } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [driveStatus, setDriveStatus] = useState({ connected: false, loading: true });
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, configured: false, loading: true });
  const [connecting, setConnecting] = useState(false);
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  
  // Get current tenant for Drive folder link
  const currentTenant = getCurrentTenantSlug();
  const driveFolderLink = DRIVE_FOLDER_LINKS[currentTenant] || DRIVE_FOLDER_LINKS["default"];
  
  // Profile edit state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', email: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Tenant AI Settings state (Super Admin only)
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [aiSettings, setAiSettings] = useState({
    ai_translation_enabled: true,
    ai_report_review_enabled: true,
    ai_model: "gpt-5-mini"
  });
  const [loadingAiSettings, setLoadingAiSettings] = useState(false);
  const [savingAiSettings, setSavingAiSettings] = useState(false);

  // Module Access Settings state (Super Admin only)
  const [moduleSettings, setModuleSettings] = useState({});
  const [allModules, setAllModules] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loadingModuleSettings, setLoadingModuleSettings] = useState(false);
  const [savingModuleSettings, setSavingModuleSettings] = useState(false);
  const [selectedModuleRole, setSelectedModuleRole] = useState("");

  // Check if current user can manage Google Drive (only super_admin)
  const isDriveAdmin = user?.role === "super_admin" || user?.email === SUPER_ADMIN_EMAIL;
  
  // Initialize profile data when user loads
  useEffect(() => {
    if (user) {
      setProfileData({ name: user.name || '', email: user.email || '' });
    }
  }, [user]);

  const checkDriveStatus = async () => {
    try {
      const response = await axios.get(`${API}/drive/status`, { withCredentials: true });
      setDriveStatus({ ...response.data, loading: false });
    } catch (error) {
      setDriveStatus({ connected: false, loading: false });
    }
  };

  const checkCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API}/calendar/status`, { withCredentials: true });
      setCalendarStatus({ ...response.data, loading: false });
    } catch (error) {
      setCalendarStatus({ connected: false, configured: false, loading: false });
    }
  };

  useEffect(() => {
    checkDriveStatus();
    checkCalendarStatus();
    
    // Check for callback params
    if (searchParams.get("drive_connected") === "true") {
      toast.success("Google Drive başarıyla bağlandı!");
      checkDriveStatus();
    }
    if (searchParams.get("drive_error")) {
      toast.error(`Google Drive bağlantı hatası: ${searchParams.get("drive_error")}`);
    }
    if (searchParams.get("calendar_connected") === "true") {
      toast.success("Google Calendar başarıyla bağlandı!");
      checkCalendarStatus();
    }
    if (searchParams.get("calendar_error")) {
      toast.error(`Google Calendar bağlantı hatası: ${searchParams.get("calendar_error")}`);
    }
  }, [searchParams]);

  const handleConnectDrive = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/drive/connect`, { withCredentials: true });
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Google Drive bağlantısı başlatılamadı");
      setConnecting(false);
    }
  };

  const handleDisconnectDrive = async () => {
    if (!window.confirm("Google Drive bağlantısını kaldırmak istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/drive/disconnect`, { withCredentials: true });
      toast.success("Google Drive bağlantısı kaldırıldı");
      setDriveStatus({ connected: false, loading: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bağlantı kaldırılamadı");
    }
  };

  const handleConnectCalendar = async () => {
    setConnectingCalendar(true);
    try {
      const response = await axios.get(`${API}/calendar/connect`, { withCredentials: true });
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Google Calendar bağlantısı başlatılamadı");
      setConnectingCalendar(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!window.confirm("Google Calendar bağlantısını kaldırmak istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/calendar/disconnect`, { withCredentials: true });
      toast.success("Google Calendar bağlantısı kaldırıldı");
      setCalendarStatus({ ...calendarStatus, connected: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bağlantı kaldırılamadı");
    }
  };

  const handleSaveProfile = async () => {
    if (!profileData.name?.trim()) {
      toast.error("İsim boş olamaz");
      return;
    }
    
    setSavingProfile(true);
    try {
      const response = await axios.put(`${API}/users/me/profile`, {
        name: profileData.name.trim(),
        email: profileData.email.trim()
      }, { withCredentials: true });
      
      // Update local user state
      if (setUser) {
        setUser(prev => ({ ...prev, name: response.data.name, email: response.data.email }));
      }
      
      toast.success("Profil güncellendi");
      setEditingProfile(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Profil güncellenemedi");
    } finally {
      setSavingProfile(false);
    }
  };

  // Load tenants for super admin
  const loadTenants = async () => {
    if (user?.role !== "super_admin") return;
    try {
      const response = await axios.get(`${API}/tenants`, { withCredentials: true });
      setTenants(response.data || []);
      if (response.data?.length > 0 && !selectedTenantId) {
        setSelectedTenantId(response.data[0].tenant_id);
      }
    } catch (error) {
      console.error("Failed to load tenants:", error);
    }
  };

  // Load AI settings for selected tenant
  const loadAiSettings = async (tenantId) => {
    if (!tenantId) return;
    setLoadingAiSettings(true);
    try {
      const response = await axios.get(`${API}/tenants/${tenantId}/ai-settings`, { withCredentials: true });
      setAiSettings(response.data);
    } catch (error) {
      console.error("Failed to load AI settings:", error);
      // Use defaults
      setAiSettings({
        ai_translation_enabled: true,
        ai_report_review_enabled: true,
        ai_model: "gpt-5-mini"
      });
    } finally {
      setLoadingAiSettings(false);
    }
  };

  // Save AI settings
  const handleSaveAiSettings = async () => {
    if (!selectedTenantId) return;
    setSavingAiSettings(true);
    try {
      await axios.put(`${API}/tenants/${selectedTenantId}/ai-settings`, aiSettings, { withCredentials: true });
      toast.success("AI ayarları kaydedildi");
    } catch (error) {
      toast.error(error.response?.data?.detail || "AI ayarları kaydedilemedi");
    } finally {
      setSavingAiSettings(false);
    }
  };

  // Load Module Settings
  const loadModuleSettings = async (tenantId) => {
    if (!tenantId) return;
    setLoadingModuleSettings(true);
    try {
      const response = await axios.get(`${API}/tenants/${tenantId}/module-settings`, { withCredentials: true });
      setModuleSettings(response.data.role_permissions || {});
      setAllModules(response.data.all_modules || []);
      setAllRoles(response.data.all_roles || []);
      if (response.data.all_roles?.length > 0 && !selectedModuleRole) {
        setSelectedModuleRole(response.data.all_roles[0]);
      }
    } catch (error) {
      console.error("Failed to load module settings:", error);
    } finally {
      setLoadingModuleSettings(false);
    }
  };

  // Save Module Settings
  const handleSaveModuleSettings = async () => {
    if (!selectedTenantId) return;
    setSavingModuleSettings(true);
    try {
      await axios.put(`${API}/tenants/${selectedTenantId}/module-settings`, {
        role_permissions: moduleSettings
      }, { withCredentials: true });
      toast.success("Modül erişim ayarları kaydedildi");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Modül ayarları kaydedilemedi");
    } finally {
      setSavingModuleSettings(false);
    }
  };

  // Toggle module access for a role
  const toggleModuleAccess = (role, modulePath) => {
    setModuleSettings(prev => {
      const roleModules = prev[role] || [];
      const newModules = roleModules.includes(modulePath)
        ? roleModules.filter(m => m !== modulePath)
        : [...roleModules, modulePath];
      return { ...prev, [role]: newModules };
    });
  };

  // Load tenants when user changes
  useEffect(() => {
    if (user?.role === "super_admin") {
      loadTenants();
    }
  }, [user]);

  // Load AI settings when tenant changes
  useEffect(() => {
    if (selectedTenantId && user?.role === "super_admin") {
      loadAiSettings(selectedTenantId);
      loadModuleSettings(selectedTenantId);
    }
  }, [selectedTenantId]);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Lütfen giriş yapın</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <h1 className="text-2xl font-bold text-[#1A4D2E] mb-6">Ayarlar</h1>

      {/* Profile Settings */}
      <Card className="mb-6" data-testid="profile-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-[#1A4D2E]" />
            Profil Bilgileri
          </CardTitle>
          <CardDescription>
            İsim ve e-posta adresinizi güncelleyebilirsiniz
          </CardDescription>
        </CardHeader>
        <CardContent>
          {editingProfile ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="profile-name">İsim Soyisim</Label>
                <Input
                  id="profile-name"
                  value={profileData.name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Adınız Soyadınız"
                  className="mt-1"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label htmlFor="profile-email">E-posta</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="mt-1"
                  data-testid="profile-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Not: Google ile giriş yapıyorsanız e-posta değişikliği giriş yönteminizi etkilemez
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleSaveProfile} 
                  disabled={savingProfile}
                  className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                  data-testid="save-profile-btn"
                >
                  {savingProfile ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Kaydet
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setEditingProfile(false);
                    setProfileData({ name: user?.name || '', email: user?.email || '' });
                  }}
                >
                  İptal
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">İsim Soyisim</p>
                  <p className="font-medium">{user?.name || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">E-posta</p>
                  <p className="font-medium">{user?.email || '-'}</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={() => setEditingProfile(true)}
                className="mt-2"
                data-testid="edit-profile-btn"
              >
                Profili Düzenle
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Google Calendar Integration */}
      <Card className="mb-6" data-testid="calendar-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Google Calendar Entegrasyonu
          </CardTitle>
          <CardDescription>
            Atanan denetimler otomatik olarak Google Calendar'ınıza eklenir
          </CardDescription>
        </CardHeader>
        <CardContent>
          {calendarStatus.loading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Kontrol ediliyor...
            </div>
          ) : !calendarStatus.configured ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                Google Calendar API henüz yapılandırılmamış. Sistem yöneticisiyle iletişime geçin.
              </p>
            </div>
          ) : calendarStatus.connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                <Check className="w-5 h-5" />
                <span className="font-medium">Google Calendar Bağlı</span>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Nasıl Çalışır:</strong> Size atanan denetimler otomatik olarak Google Calendar'ınıza eklenir. Denetim tarihi değiştiğinde takvim etkinliği güncellenir.
                </p>
              </div>

              <Button 
                variant="outline" 
                onClick={handleDisconnectCalendar}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Unlink className="w-4 h-4 mr-2" />
                Bağlantıyı Kaldır
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  Google Calendar'ınızı bağlayarak atanan denetimlerin otomatik olarak takviminize eklenmesini sağlayın.
                </p>
              </div>

              <Button 
                onClick={handleConnectCalendar}
                disabled={connectingCalendar}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {connectingCalendar ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Bağlanıyor...
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4 mr-2" />
                    Google Calendar Bağla
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Google Drive Integration - Only visible to super_admin */}
      {isDriveAdmin && (
        <Card className="mb-6" data-testid="drive-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardDrive className="w-5 h-5" />
              Google Drive Entegrasyonu
            </CardTitle>
            <CardDescription>
              Onaylanan raporlar otomatik olarak Google Drive'a yüklenir
            </CardDescription>
          </CardHeader>
          <CardContent>
            {driveStatus.loading ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Kontrol ediliyor...
              </div>
            ) : driveStatus.connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">Google Drive Bağlı</span>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Nasıl Çalışır:</strong> Herhangi bir rapor onaylandığında, tüm belgeler otomatik olarak Google Drive&apos;a yüklenir. Klasör adı: &quot;[Müşteri Adı] - [Tarih]&quot;
                  </p>
                </div>

                {isDriveAdmin && (
                  <div className="flex items-center gap-3">
                    <a 
                      href={driveFolderLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Drive Klasörünü Aç
                    </a>
                    
                    <Button 
                      variant="outline" 
                      onClick={handleDisconnectDrive}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Unlink className="w-4 h-4 mr-2" />
                      Bağlantıyı Kaldır
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {isDriveAdmin ? (
                  <>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        Google Drive bağlı değil. Raporlar onaylandığında otomatik yükleme yapılabilmesi için bağlantı yapmanız gerekmektedir.
                      </p>
                    </div>

                    <Button 
                      onClick={handleConnectDrive}
                      disabled={connecting}
                      className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                    >
                      {connecting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Bağlanıyor...
                        </>
                      ) : (
                        <>
                          <Link2 className="w-4 h-4 mr-2" />
                          Google Drive Bağla
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600">
                      Google Drive henüz bağlanmamış. Sistem yöneticisi tarafından bağlanması gerekmektedir.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tenant AI Settings - Super Admin Only */}
      {user?.role === "super_admin" && (
        <Card data-testid="ai-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-blue-600" />
              Tenant AI Ayarları
            </CardTitle>
            <CardDescription>
              OpenAI GPT-5 mini ile çeviri ve rapor inceleme özelliklerini tenant bazında yönetin
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Tenant Selector */}
            <div className="space-y-2">
              <Label>Tenant Seçin</Label>
              <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                <SelectTrigger data-testid="tenant-selector">
                  <SelectValue placeholder="Tenant seçin" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {tenant.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loadingAiSettings ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : selectedTenantId ? (
              <div className="space-y-6 border rounded-lg p-4 bg-gray-50">
                {/* AI Translation Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">AI Çeviri</Label>
                    <p className="text-sm text-gray-500">
                      Türkçe-İngilizce otomatik çeviri (OpenAI GPT-5 mini)
                    </p>
                  </div>
                  <Switch
                    checked={aiSettings.ai_translation_enabled}
                    onCheckedChange={(checked) => setAiSettings({...aiSettings, ai_translation_enabled: checked})}
                    data-testid="ai-translation-toggle"
                  />
                </div>

                {/* AI Report Review Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">AI Rapor İnceleme</Label>
                    <p className="text-sm text-gray-500">
                      Tamlık, tutarlılık, dil kalitesi ve standart uyumu kontrolü
                    </p>
                  </div>
                  <Switch
                    checked={aiSettings.ai_report_review_enabled}
                    onCheckedChange={(checked) => setAiSettings({...aiSettings, ai_report_review_enabled: checked})}
                    data-testid="ai-review-toggle"
                  />
                </div>

                {/* Model Selection */}
                <div className="space-y-2">
                  <Label>AI Model</Label>
                  <Select 
                    value={aiSettings.ai_model} 
                    onValueChange={(value) => setAiSettings({...aiSettings, ai_model: value})}
                  >
                    <SelectTrigger data-testid="ai-model-selector">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gpt-5-mini">GPT-5 Mini (Önerilen)</SelectItem>
                      <SelectItem value="gpt-5">GPT-5</SelectItem>
                      <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400">
                    GPT-5 Mini maliyet-performans açısından önerilir
                  </p>
                </div>

                {/* Save Button */}
                <Button 
                  onClick={handleSaveAiSettings}
                  disabled={savingAiSettings}
                  className="w-full bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                  data-testid="save-ai-settings-btn"
                >
                  {savingAiSettings ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      AI Ayarlarını Kaydet
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Bir tenant seçin</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Module Access Settings - Super Admin Only */}
      {user?.role === "super_admin" && (
        <Card className="mb-6" data-testid="module-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#1A4D2E]" />
              Modül Erişim Ayarları
            </CardTitle>
            <CardDescription>
              Her rol için hangi modüllere erişim izni verileceğini ayarlayın
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Tenant selector - reuse the same selection */}
            {selectedTenantId ? (
              loadingModuleSettings ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#1A4D2E]" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Role tabs */}
                  <div className="flex flex-wrap gap-2 border-b pb-4">
                    {allRoles.map(role => (
                      <Button
                        key={role}
                        variant={selectedModuleRole === role ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedModuleRole(role)}
                        className={selectedModuleRole === role ? "bg-[#1A4D2E]" : ""}
                      >
                        {role === 'admin' ? 'Yönetici' :
                         role === 'technical_manager' ? 'Teknik Yönetici' :
                         role === 'planning_expert' ? 'Planlama Uzmanı' :
                         role === 'auditor' ? 'Denetçi' :
                         role === 'sales_representative' ? 'Satış Temsilcisi' : role}
                      </Button>
                    ))}
                  </div>

                  {/* Module checkboxes for selected role */}
                  {selectedModuleRole && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {allModules.map(module => {
                        const isEnabled = moduleSettings[selectedModuleRole]?.includes(module.path);
                        return (
                          <div
                            key={module.path}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                              isEnabled 
                                ? 'bg-green-50 border-green-300' 
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                            }`}
                            onClick={() => toggleModuleAccess(selectedModuleRole, module.path)}
                          >
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={() => toggleModuleAccess(selectedModuleRole, module.path)}
                            />
                            <span className={`text-sm font-medium ${isEnabled ? 'text-green-800' : 'text-gray-600'}`}>
                              {module.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Save button */}
                  <div className="flex justify-end pt-4 border-t">
                    <Button
                      onClick={handleSaveModuleSettings}
                      disabled={savingModuleSettings}
                      className="bg-[#1A4D2E] hover:bg-[#2A5D3E]"
                    >
                      {savingModuleSettings ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Kaydediliyor...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Modül Ayarlarını Kaydet
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )
            ) : (
              <p className="text-gray-500 text-center py-4">Yukarıdan bir tenant seçin</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* User Info */}
      <Card data-testid="user-info-card">
        <CardHeader>
          <CardTitle>Kullanıcı Bilgileri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">Ad Soyad</span>
              <span className="font-medium">{user.name}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">E-posta</span>
              <span className="font-medium">{user.email}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">Rol</span>
              <span className="font-medium capitalize">
                {user.role === 'super_admin' ? 'Süper Admin' :
                 user.role === 'admin' ? 'Yönetici' : 
                 user.role === 'technical_manager' ? 'Teknik Yönetici' :
                 user.role === 'planning_expert' ? 'Planlama Uzmanı' :
                 user.role === 'auditor' ? 'Denetçi' : user.role}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
